import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail } from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const SECTIONS = [
  {
    id: "meaning",
    title: "Meaning of Personal Information",
    body: [
      "\"Personal Information\" means information about an identifiable individual. Depending on the circumstances, this may include your:",
      { list: [
        "name",
        "mailing address",
        "billing address",
        "shipping address",
        "email address",
        "telephone number",
        "account information",
        "payment or transaction information",
        "order history",
        "device and browser information",
        "customer support communications",
      ]},
      "In some circumstances, information related to wellness, supplementation interests, or product usage preferences may be considered sensitive information under applicable privacy laws.",
      "Personal Information does not include: business contact information used solely in relation to employment or business activities; or anonymized or aggregated information that cannot reasonably be used to identify an individual.",
    ],
  },
  {
    id: "consent",
    title: "Your Consent to Collection, Use and Disclosure",
    body: [
      "We collect, use, and disclose your Personal Information with your consent or as otherwise permitted or required by applicable law, including Canadian privacy legislation such as the Personal Information Protection and Electronic Documents Act (PIPEDA).",
      "Your consent may be express or implied depending on the sensitivity of the information and the circumstances.",
      "By voluntarily providing Personal Information to us, including through our website, purchases, customer support, or communications, you consent to the collection, use, disclosure, and retention of that information as described in this Privacy Statement.",
      "You may withdraw your consent at any time, subject to legal or contractual restrictions and reasonable notice. Withdrawal of consent may limit our ability to provide products or services to you.",
      "If you provide us with Personal Information about another person, you confirm that you have obtained the necessary consent to do so.",
    ],
  },
  {
    id: "collect",
    title: "Personal Information We Collect",
    body: [
      { h: "Products and Services" },
      "When you purchase or use our products or services, we may collect:",
      { list: [
        "your name",
        "billing and shipping addresses",
        "email address",
        "telephone number",
        "account credentials",
        "order and transaction information",
        "customer service communications",
        "subscription information",
      ]},
      "We may also collect information you voluntarily provide relating to your product preferences, wellness interests, or customer support inquiries.",
      "Our products and services are not intended to provide medical advice, diagnosis, or treatment. Please do not provide medical records or detailed health information unless specifically requested for lawful business purposes.",
      { h: "Website Information" },
      "When you visit our website, we may automatically collect certain technical information, including:",
      { list: [
        "IP address",
        "browser type",
        "device information",
        "operating system",
        "pages visited",
        "referral URLs",
        "date and time of access",
      ]},
      "We may use cookies, pixels, analytics tools, and similar technologies to improve website functionality, remember user preferences, analyze website traffic, personalize user experiences, and support advertising and remarketing activities.",
      "Most browsers allow you to disable cookies; however, certain website features may not function properly if cookies are disabled.",
      { h: "Marketing and Communications" },
      "If you subscribe to newsletters, promotional materials, SMS messages, or other communications, we may collect your contact information and communication preferences.",
      "By providing your phone number, you consent to receive transactional or marketing communications from us, including SMS messages where permitted by law. Consent is not a condition of purchase. Message and data rates may apply. You may opt out at any time.",
      { h: "Payment Information" },
      "We do not directly store full payment card information on our systems unless you provide it directly to us through approved methods.",
      "Payments processed through our website are handled by third-party payment processors. Those providers collect, process, and store payment information in accordance with their own privacy policies and security practices.",
    ],
  },
  {
    id: "use",
    title: "How We Use Your Personal Information",
    body: [
      "We may use your Personal Information for purposes including:",
      { list: [
        "processing and fulfilling orders",
        "providing products and services",
        "managing customer accounts",
        "communicating with you regarding orders or services",
        "customer support",
        "verifying transactions",
        "improving website functionality and user experience",
        "analytics and business operations",
        "marketing and promotional communications",
        "administering contests, surveys, or promotions",
        "detecting fraud or unauthorized activity",
        "enforcing agreements and policies",
        "complying with legal obligations",
        "protecting our business, customers, and operations",
      ]},
      "We may also create aggregated or de-identified information for analytics, research, or operational purposes.",
      "We do not sell Personal Information to third parties.",
    ],
  },
  {
    id: "advertising",
    title: "Advertising, Analytics and Cookies",
    body: [
      "We may use third-party analytics and advertising providers, including platforms such as Google, Meta, Shopify, or similar providers, to better understand website usage and deliver relevant advertising.",
      "These providers may use cookies, pixels, and similar technologies to collect information about your interactions with our website and other websites.",
      "You can manage cookie settings through your browser and may opt out of certain targeted advertising programs through applicable industry opt-out tools.",
    ],
  },
  {
    id: "share",
    title: "How We Share Your Personal Information",
    body: [
      "We may share your Personal Information with trusted third-party service providers that assist us with payment processing, order fulfillment, website hosting, customer service, analytics, marketing communications, shipping and logistics, fraud prevention, and technical support.",
      "These service providers are only authorized to use your Personal Information as necessary to provide services to us.",
      "We may also disclose Personal Information: where required or permitted by law; to comply with legal processes or government requests; to enforce agreements or protect our rights; to investigate fraud, security issues, or unlawful activities; or as part of a merger, acquisition, financing, or sale of assets.",
      "We do not authorize third parties to use your Personal Information for their own direct marketing purposes.",
    ],
  },
  {
    id: "opt-out",
    title: "Opting Out of Communications",
    body: [
      "You may unsubscribe from marketing emails at any time by clicking the \"unsubscribe\" link in our emails or contacting us directly.",
      "You may opt out of SMS communications by following the instructions provided in the message.",
      "Please note that even if you opt out of marketing communications, we may still send transactional or service-related communications.",
    ],
  },
  {
    id: "retention",
    title: "Retention of Personal Information",
    body: [
      "We retain Personal Information only for as long as reasonably necessary to fulfill the purposes described in this Privacy Statement, comply with legal obligations, resolve disputes, and enforce agreements.",
      "When Personal Information is no longer required, we will securely destroy, anonymize, or dispose of it in accordance with applicable laws and internal retention practices.",
    ],
  },
  {
    id: "security",
    title: "Information Security",
    body: [
      "We implement reasonable physical, administrative, organizational, contractual, and technological safeguards designed to protect Personal Information against unauthorized access, disclosure, misuse, loss, or theft.",
      "Access to Personal Information is restricted to personnel and service providers who require access for legitimate business purposes.",
      "Despite these safeguards, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
      "In the event of a security breach involving Personal Information that poses a real risk of significant harm, we will notify affected individuals and applicable regulators as required by law.",
    ],
  },
  {
    id: "rights",
    title: "Your Privacy Rights",
    body: [
      "Subject to applicable law, you may have the right to:",
      { list: [
        "access Personal Information we hold about you",
        "request corrections to inaccurate information",
        "withdraw consent to certain uses of your information",
        "request deletion of Personal Information where applicable",
        "file a complaint with a privacy regulator",
      ]},
      "To exercise your rights, please contact us using the information below.",
      "We may require verification of your identity before processing certain requests.",
    ],
  },
  {
    id: "transfer",
    title: "International Transfer and Storage of Information",
    body: [
      "Your Personal Information may be processed or stored in Canada, the United States, or other jurisdictions where we or our service providers operate.",
      "Information transferred outside your jurisdiction may become subject to the laws of foreign jurisdictions and may be accessible to courts, governments, or regulatory authorities in those jurisdictions.",
    ],
  },
  {
    id: "third-party",
    title: "Third Party Websites and Services",
    body: [
      "Our website may contain links to third-party websites or services. We are not responsible for the privacy practices or content of third-party websites or services.",
      "We encourage you to review their privacy policies before providing Personal Information.",
    ],
  },
  {
    id: "children",
    title: "Children's Information",
    body: [
      "Our products and services are intended only for individuals who are at least 18 years of age.",
      "We do not knowingly collect Personal Information from individuals under the age of 18.",
      "By using our website or purchasing our products, you represent that you are at least 18 years old.",
    ],
  },
];

function Body({ body }) {
  return body.map((b, i) => {
    if (typeof b === "string") {
      return <p key={i} className="mt-4 text-[15px] leading-[1.75] text-[#C9C9C9]">{b}</p>;
    }
    if (b.h) {
      return <h3 key={i} className="mt-8 mb-2 font-display text-lg text-[#F5F5F5]">{b.h}</h3>;
    }
    if (b.list) {
      return (
        <ul key={i} className="mt-3 mb-1 pl-5 space-y-1.5">
          {b.list.map((item, j) => (
            <li key={j} className="text-[14px] leading-[1.7] text-[#C9C9C9] list-disc marker:text-[#4ADE80]">{item}</li>
          ))}
        </ul>
      );
    }
    return null;
  });
}

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10] text-[#E5E5E5]">
      <SEO
        title="Privacy Statement"
        description="EndaG1 Privacy Statement — how we collect, use and protect your data when you use our research peptide platform."
        canonicalPath="/privacy"
      />
      <section className="mx-auto max-w-[900px] w-full px-5 sm:px-8 py-14 sm:py-20 flex-1">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8] hover:text-white transition-colors mb-8" data-testid="privacy-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#4ADE80]">Legal</p>
          <h1 className="mt-4 font-serif-editorial text-4xl sm:text-5xl md:text-6xl text-[#F5F5F5] leading-tight">Privacy Statement</h1>
          <p className="mt-4 text-sm text-[#7a7a7a]">Last Updated: May 7, 2026</p>
        </motion.div>

        <div className="mt-10 sm:mt-14 space-y-6">
          <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
            EndaG1 Inc. and its affiliates (collectively, &ldquo;EndaG1&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo; or &ldquo;us&rdquo;) recognize the importance of protecting your privacy and Personal Information. The purpose of this Privacy Statement (&ldquo;Privacy Statement&rdquo;) is to explain how we collect, use, disclose, store, and protect your Personal Information when you interact with us, including through our website, products, services, customer support channels, and other interactions.
          </p>
          <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
            This Privacy Statement applies to all of our operations in Canada or operations connected to Canada unless a separate privacy statement applies to a specific product, service, or activity.
          </p>
          <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
            By using our website, purchasing our products, creating an account, subscribing to communications, or otherwise interacting with us, you consent to the collection, use, disclosure, and retention of your Personal Information as described in this Privacy Statement.
          </p>
        </div>

        {/* Updates block */}
        <section className="mt-12 border-l-2 border-[#4ADE80]/40 pl-5">
          <h2 className="font-display text-2xl text-[#F5F5F5]">Privacy Statement Updates</h2>
          <p className="mt-3 text-[15px] leading-[1.75] text-[#C9C9C9]">
            This Privacy Statement is current as of the &ldquo;Last Updated&rdquo; date listed above. We may update this Privacy Statement from time to time to reflect changes in our business practices, technologies, legal requirements, or other developments.
          </p>
          <p className="mt-3 text-[15px] leading-[1.75] text-[#C9C9C9]">
            When changes are made, the revised Privacy Statement will be posted on our website and will become effective immediately unless otherwise stated. Your continued use of our website, products, or services following the posting of changes constitutes acceptance of the revised Privacy Statement.
          </p>
        </section>

        {/* Table of contents */}
        <nav aria-label="Table of contents" data-testid="privacy-toc" className="mt-14 border border-white/10 bg-[#171717] p-6 sm:p-7">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4ADE80] mb-3">What This Statement Covers</div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm counter-reset-list">
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-[#C9C9C9] hover:text-[#4ADE80] transition-colors">
                  <span className="text-[#7a7a7a] font-mono mr-2">{String(i + 1).padStart(2, "0")}.</span>
                  {s.title}
                </a>
              </li>
            ))}
            <li>
              <a href="#contact" className="text-[#C9C9C9] hover:text-[#4ADE80] transition-colors">
                <span className="text-[#7a7a7a] font-mono mr-2">{String(SECTIONS.length + 1).padStart(2, "0")}.</span>
                How to Contact Us
              </a>
            </li>
          </ol>
        </nav>

        {/* Sections */}
        <div className="mt-16 space-y-16">
          {SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`} className="scroll-mt-24">
              <div className="flex items-baseline gap-3">
                <span className="text-[11px] font-mono text-[#4ADE80]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-2xl sm:text-3xl text-[#F5F5F5] tracking-tight">{s.title}</h2>
              </div>
              <div className="mt-3 pl-9">
                <Body body={s.body} />
              </div>
            </section>
          ))}

          {/* Contact */}
          <section id="contact" data-testid="privacy-section-contact" className="scroll-mt-24">
            <div className="flex items-baseline gap-3">
              <span className="text-[11px] font-mono text-[#4ADE80]">{String(SECTIONS.length + 1).padStart(2, "0")}</span>
              <h2 className="font-display text-2xl sm:text-3xl text-[#F5F5F5] tracking-tight">How to Contact Us</h2>
            </div>
            <div className="mt-3 pl-9">
              <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
                If you have questions, concerns, requests, or complaints regarding this Privacy Statement or our privacy practices, please contact our Privacy Officer:
              </p>
              <div className="mt-6 border border-white/10 bg-[#171717] p-6 max-w-md">
                <div className="font-display text-lg text-[#F5F5F5]">EndaG1 Inc.</div>
                <div className="mt-1 text-sm text-[#A8A8A8]">Privacy Officer</div>
                <Link to="/#contact" data-testid="privacy-contact-form-link" className="mt-4 inline-flex items-center gap-2 bg-[#4ADE80] text-[#0F0F10] px-5 py-2.5 uppercase tracking-[0.14em] text-[11px] hover:bg-[#6FE998] transition-colors">
                  <Mail className="h-4 w-4" /> Contact Us
                </Link>
                <a href="mailto:info@endag1.com" className="mt-3 block text-sm text-[#7a7a7a] hover:text-[#4ADE80] transition-colors" data-testid="privacy-contact-email">info@endag1.com</a>
              </div>
            </div>
          </section>
        </div>
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
